import { fmtNum } from '../lib/format.js';

export function DailyForecastBars({ rows, actuals, weekStart, weekEnd }) {
  // Build a Mon→Sun array of forecast values keyed on the week's start
  // date. The wizard's hourlyForecast rolls up server-side into per-date
  // totals; here we just align them to the calendar week and render bars.
  // CJ 2026-05-16: render actual bars alongside projected for past days
  // (and partial today) when `actuals` is populated. Past actual bars
  // overlay the projected bar so over/under is read at a glance.
  if (!rows || rows.length === 0) {
    // Build empty rows so the chart frame still renders for context.
    return (
      <div className="card">
        <div style={{ fontSize: 13, fontWeight: 700, color: '#212121', marginBottom: 6 }}>
          Daily forecast — projected vs actual
        </div>
        <div style={{ fontSize: 12, color: '#757575' }}>
          No daily forecast available for this week yet.
        </div>
      </div>
    );
  }
  const byDate = new Map();
  for (const r of rows) byDate.set(r.date, Number(r.projected_transactions) || 0);
  const actualsMap = actuals || {};
  // Generate Mon..Sun for the active week
  const days = [];
  const d = new Date(weekStart + 'T12:00:00Z');
  for (let i = 0; i < 7; i++) {
    const ymd = new Intl.DateTimeFormat('en-CA', {
      timeZone: 'America/New_York',
      year: 'numeric', month: '2-digit', day: '2-digit',
    }).format(d);
    const a = actualsMap[ymd];
    days.push({
      ymd,
      label: ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'][i],
      mmdd: ymd.slice(5),
      tx: byDate.get(ymd) || 0,
      actual: a ? Number(a.tx) || 0 : null,
      isToday: !!(a && a.isToday),
    });
    d.setUTCDate(d.getUTCDate() + 1);
  }
  const max = Math.max(1, ...days.map((x) => Math.max(x.tx, x.actual || 0)));
  function niceMax(v) {
    if (v <= 100) return Math.ceil(v / 50) * 50;
    if (v <= 1000) return Math.ceil(v / 100) * 100;
    if (v <= 5000) return Math.ceil(v / 500) * 500;
    return Math.ceil(v / 1000) * 1000;
  }
  const yMax = niceMax(max);
  const total = days.reduce((s, x) => s + x.tx, 0);
  const actualTotal = days.reduce((s, x) => s + (x.actual ?? 0), 0);
  const W = 720, H = 200, PL = 36, PR = 12, PT = 12, PB = 36;
  const innerW = W - PL - PR, innerH = H - PT - PB;
  const barW = innerW / 7 * 0.7;
  const slot = innerW / 7;
  const yTicks = [0, 0.5, 1].map((f) => Math.round(yMax * f));
  return (
    <div className="card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8, flexWrap: 'wrap', gap: 8 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#212121' }}>
          Daily forecast — projected vs actual
        </div>
        <div style={{ fontSize: 11, color: '#757575' }}>
          Projected {fmtNum(total)}{actualTotal > 0 ? ` · actual ${fmtNum(actualTotal)}` : ''} · {weekStart} → {weekEnd}
        </div>
      </div>
      <div style={{ display: 'flex', gap: 12, marginBottom: 6, fontSize: 11, color: '#555' }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          <span style={{ width: 10, height: 10, background: '#cfe2f3', borderRadius: 2, display: 'inline-block' }} />
          Projected
        </span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          <span style={{ width: 10, height: 10, background: '#1565c0', borderRadius: 2, display: 'inline-block' }} />
          Actual
        </span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          <span style={{ width: 10, height: 10, background: '#1565c0', opacity: 0.45, borderRadius: 2, display: 'inline-block' }} />
          Actual (today, partial)
        </span>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 200, display: 'block' }}>
        {yTicks.map((v, i) => {
          const y = PT + innerH - (v / yMax) * innerH;
          return (
            <g key={i}>
              <line x1={PL} y1={y} x2={W - PR} y2={y} stroke="#eeeeee" strokeWidth="1" />
              <text x={PL - 4} y={y + 3} fontSize="10" fill="#9e9e9e" textAnchor="end">{v.toLocaleString()}</text>
            </g>
          );
        })}
        {days.map((day, i) => {
          const cx = PL + slot * i + slot / 2;
          const hProj = (day.tx / yMax) * innerH;
          const xProj = cx - barW / 2;
          const yProj = PT + innerH - hProj;
          const hasActual = day.actual != null;
          const hActual = hasActual ? (day.actual / yMax) * innerH : 0;
          const xActual = cx - (barW * 0.45);
          const yActual = PT + innerH - hActual;
          const actualWidth = barW * 0.9;
          return (
            <g key={day.ymd}>
              {/* Projected (always shown) — lighter color */}
              <rect x={xProj} y={yProj} width={barW} height={Math.max(0, hProj)}
                    fill="#cfe2f3" rx="3" ry="3">
                <title>{`${day.label} ${day.mmdd}: projected ${day.tx.toLocaleString()} tx${hasActual ? ` · actual ${day.actual.toLocaleString()}${day.isToday ? ' (today, in progress)' : ''}` : ''}`}</title>
              </rect>
              {/* Actual overlay — solid forest blue, dimmed for today */}
              {hasActual && (
                <rect x={xActual} y={yActual} width={actualWidth} height={Math.max(0, hActual)}
                      fill="#1565c0" rx="3" ry="3"
                      opacity={day.isToday ? 0.45 : 1}>
                  <title>{`${day.label} ${day.mmdd}: actual ${day.actual.toLocaleString()} tx${day.isToday ? ' (today, in progress)' : ''}`}</title>
                </rect>
              )}
              {day.tx > 0 && hProj > 16 && (
                <text x={cx} y={Math.min(yProj, yActual || yProj) - 4} fontSize="10" fill="#0d47a1" fontWeight="600" textAnchor="middle">
                  {hasActual ? day.actual.toLocaleString() : day.tx.toLocaleString()}
                </text>
              )}
              <text x={cx} y={H - 18} fontSize="11" fill="#37474f" fontWeight="600" textAnchor="middle">
                {day.label}
              </text>
              <text x={cx} y={H - 6} fontSize="10" fill="#9e9e9e" textAnchor="middle">
                {day.mmdd}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}
